import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'bookingflows_cookie_consent';

/**
 * Tidio Chat Widget Controller
 * 
 * Place this component in App.jsx (at the root level).
 * It loads Tidio once but only SHOWS it on the landing page AFTER cookie consent is ACCEPTED.
 * On all other pages, the widget is hidden.
 */
const TidioController = () => {
  const location = useLocation();
  const [cookieConsentAccepted, setCookieConsentAccepted] = useState(false);
  
  // Check if cookie consent was ACCEPTED (not just handled)
  useEffect(() => {
    const checkConsent = () => {
      try {
        const consentStr = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (consentStr) {
          const consent = JSON.parse(consentStr);
          // Check if user accepted cookies (not declined)
          setCookieConsentAccepted(consent.accepted === true);
        } else {
          setCookieConsentAccepted(false);
        }
      } catch (e) {
        // If parsing fails, check if it's a simple truthy value
        const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
        setCookieConsentAccepted(consent === 'true' || consent === 'accepted');
      }
    };
    
    // Check immediately
    checkConsent();
    
    // Listen for storage changes (when user accepts/declines cookies)
    const handleStorageChange = (e) => {
      if (e.key === COOKIE_CONSENT_KEY) {
        checkConsent();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Also poll for changes (for same-tab updates)
    const interval = setInterval(checkConsent, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);
  
  useEffect(() => {
    // Only show on landing page (root path) AND after cookie consent is ACCEPTED
    const isLandingPage = location.pathname === '/' || location.pathname === '';
    const shouldShowTidio = isLandingPage && cookieConsentAccepted;
    
    // Function to hide Tidio completely
    const hideTidio = () => {
      // Use Tidio API if available
      if (window.tidioChatApi) {
        try {
          window.tidioChatApi.hide();
        } catch (e) {}
      }
      // Force hide all Tidio elements
      const tidioElements = document.querySelectorAll(
        '#tidio-chat, #tidio-chat-iframe, [id^="tidio"], iframe[src*="tidio"]'
      );
      tidioElements.forEach(el => {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
      });
    };
    
    // Function to show Tidio
    const showTidio = () => {
      // Use Tidio API if available
      if (window.tidioChatApi) {
        try {
          window.tidioChatApi.show();
        } catch (e) {}
      }
      // Force show all Tidio elements
      const tidioElements = document.querySelectorAll(
        '#tidio-chat, #tidio-chat-iframe, [id^="tidio"], iframe[src*="tidio"]'
      );
      tidioElements.forEach(el => {
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('opacity', '1', 'important');
      });
    };
    
    // Load Tidio script if not loaded yet (but keep hidden until consent)
    if (!document.getElementById('tidio-script')) {
      const script = document.createElement('script');
      script.id = 'tidio-script';
      script.src = '//code.tidio.co/3x4vvxds4vbtnjh5c10m6w85ebcr5h4l.js';
      script.async = true;
      document.body.appendChild(script);
      
      // After script loads, apply visibility based on current state
      script.onload = () => {
        // Start hidden, then show after a delay if conditions are met
        hideTidio();
        setTimeout(() => {
          if (shouldShowTidio) {
            showTidio();
          }
        }, 500);
      };
    }
    
    // Apply visibility based on conditions
    if (shouldShowTidio) {
      // Small delay to let Tidio fully initialize
      setTimeout(showTidio, 300);
    } else {
      hideTidio();
    }
    
    // Also set up a MutationObserver to catch when Tidio injects elements
    const observer = new MutationObserver(() => {
      if (!shouldShowTidio) {
        hideTidio();
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      observer.disconnect();
    };
  }, [location.pathname, cookieConsentAccepted]);

  return null;
};

export default TidioController;
